import { useState } from 'react';
import { ChevronDown, Filter, RotateCcw } from 'lucide-react';
import { COMPANIES } from '../lib/constants';
import type { FilterState } from '../types';
import { MultiSelect } from './MultiSelect';

/**
 * Sidebar filtres du module KPI consolidés (souscriptions only).
 *
 * Sections pliables :
 *  - Période (ouverte par défaut ; masquable via `hidePeriodSection`)
 *  - Compagnies (ouverte par défaut)
 *
 * `hidePeriodSection` est utilisé par le tab « Adélie » dans
 * `KpiDirectionPage` : le filtre Période vit au niveau du shell parent,
 * les dates sont passées en props à `KpiConsolidesContent` et cette
 * sidebar n'affiche que Compagnies.
 *
 * Réinit : bouton visible uniquement si au moins un filtre est actif.
 */
export interface FiltersProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  defaults: FilterState;
  hidePeriodSection?: boolean;
}

export function Filters({ value, onChange, defaults, hidePeriodSection }: FiltersProps) {
  const [openPeriod, setOpenPeriod] = useState(true);
  const [openCompanies, setOpenCompanies] = useState(true);

  // Quand la section Période est masquée, les dates viennent du parent et
  // ne doivent pas compter dans le badge de filtres actifs (elles sont
  // "inertes" côté UI de cette sidebar).
  const activeCount =
    value.companies.length +
    (!hidePeriodSection && value.dateFrom !== defaults.dateFrom ? 1 : 0) +
    (!hidePeriodSection && value.dateTo !== defaults.dateTo ? 1 : 0);

  const hasActive = activeCount > 0;

  return (
    <div className="rounded-stripe border border-stripe-border bg-white shadow-stripe-sm">
      <div className="flex items-center justify-between border-b border-stripe-border-light px-4 py-3">
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-stripe-text-muted" />
          <span className="text-sm font-medium text-stripe-text">Filtres</span>
          {hasActive && (
            <span className="ml-1 rounded-full bg-stripe-accent-bg px-1.5 py-0.5 text-2xs font-medium text-stripe-accent">
              {activeCount}
            </span>
          )}
        </div>
      </div>

      {/* Section Période */}
      {!hidePeriodSection && (
        <Section title="Période" open={openPeriod} onToggle={() => setOpenPeriod((v) => !v)}>
          <div className="space-y-2">
            <div>
              <label className="block text-2xs font-medium text-stripe-text-muted">Du</label>
              <input
                type="date"
                value={value.dateFrom}
                onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
                className="mt-1 w-full rounded-md border border-stripe-border bg-white px-2 py-1.5 text-sm text-stripe-text focus:border-stripe-accent focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-2xs font-medium text-stripe-text-muted">Au</label>
              <input
                type="date"
                value={value.dateTo}
                onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
                className="mt-1 w-full rounded-md border border-stripe-border bg-white px-2 py-1.5 text-sm text-stripe-text focus:border-stripe-accent focus:outline-none"
              />
            </div>
          </div>
        </Section>
      )}

      {/* Section Compagnies */}
      <Section title="Compagnies" open={openCompanies} onToggle={() => setOpenCompanies((v) => !v)}>
        <MultiSelect
          options={COMPANIES}
          selected={value.companies}
          onChange={(next) => onChange({ ...value, companies: next })}
        />
      </Section>

      {hasActive && (
        <div className="border-t border-stripe-border-light px-4 py-3">
          <button
            type="button"
            onClick={() => onChange(defaults)}
            className="inline-flex items-center gap-1.5 text-2xs font-medium text-stripe-text-secondary hover:text-stripe-text"
          >
            <RotateCcw className="h-3 w-3" />
            Réinitialiser
          </button>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-stripe-border-light last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
          {title}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-stripe-text-muted transition-transform duration-200 ${
            open ? '' : '-rotate-90'
          }`}
        />
      </button>
      <div
        className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0">
          <div className="px-4 pb-3">{children}</div>
        </div>
      </div>
    </div>
  );
}
